import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@leadsnavi/ui-kit';
import cn from 'classnames';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import React from 'react';
import styles from './index.module.scss';

export interface PaginationProps {
  /** Current page number, starting from 1 */
  page: number;
  /** Page change callback */
  onPageChange: (page: number) => void;
  /** Total number of items */
  total: number;
  /** Number of items per page, default 10 */
  pageSize?: number;
  /** Number of page buttons to show, default 5 */
  showSizeChanger?: boolean;
  /** Whether to show quick jumper, default false */
  showQuickJumper?: boolean;
  /** Whether to show total info, default true */
  showTotal?: boolean | ((total: number, range: [number, number]) => React.ReactNode);
  /** Whether disabled, default false */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Number of page buttons to show, default 7 */
  siblingCount?: number;
  /**一页也展示分页 */
  showOnePage?: boolean;
}

const hoverBgColor = 'cursor-pointer hover:bg-[rgba(229,229,229,0.50)]';
export function BasePagination({
  page,
  onPageChange,
  total,
  pageSize = 10,
  showTotal = false,
  disabled = false,
  showOnePage = false,
  className,
  siblingCount = 1,
}: PaginationProps) {
  // Calculate total pages
  const totalPages = Math.ceil(total / pageSize);

  // Don't show pagination if total pages <= 1
  if (!showOnePage && totalPages <= 1) {
    return null;
  }

  // Calculate current page data range
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  // Generate page numbers array
  const getPageNumbers = () => {
    const pages: (number | 'prev-ellipsis' | 'next-ellipsis')[] = [];

    // Always show first page
    pages.push(1);

    // Calculate display range
    const startPage = Math.max(2, page - siblingCount);
    const endPage = Math.min(totalPages - 1, page + siblingCount);

    // Add ellipsis if start page > 2
    if (startPage > 2) {
      pages.push('prev-ellipsis');
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis if end page < second to last page
    if (endPage < totalPages - 1) {
      pages.push('next-ellipsis');
    }

    // Always show last page (if total pages > 1)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageChange = (newPage: number) => {
    if (disabled || newPage < 1 || newPage > totalPages || newPage === page) {
      return;
    }
    onPageChange(newPage);
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`flex items-center select-none ${className || ''}`}>
      {/* Total info */}
      {showTotal && (
        <div className="text-sm text-gray-600 flex-none">
          {typeof showTotal === 'function'
            ? showTotal(total, [startItem, endItem])
            : `Total ${total} items, showing ${startItem} - ${endItem}`}
        </div>
      )}

      {/* Pagination component */}
      <Pagination className="justify-end">
        <PaginationContent>
          {/* Previous page */}
          <PaginationItem>
            <PaginationPrevious
              onClick={() => handlePageChange(page - 1)}
              className={cn(disabled || page <= 1 ? 'pointer-events-none opacity-50' : hoverBgColor, 'w-[38px]')}
            />
          </PaginationItem>

          {/* Page numbers */}
          {pageNumbers.map((pageNum, index) => (
            <PaginationItem key={index}>
              {pageNum === 'prev-ellipsis' || pageNum === 'next-ellipsis' ? (
                <span
                  className={cn(styles['more'], hoverBgColor)}
                  onClick={() => {
                    if (pageNum === 'prev-ellipsis') {
                      const next = page - (siblingCount * 2 + 1);
                      handlePageChange(next >= 1 ? next : 1);
                    } else {
                      const next = page + (siblingCount * 2 + 1);
                      handlePageChange(next <= totalPages ? next : totalPages);
                    }
                  }}
                >
                  <PaginationEllipsis className={cn(styles['ellipsis'])} />
                  {pageNum === 'prev-ellipsis' ? <ChevronsLeft className={cn(styles['jump'])} size={16} /> : null}
                  {pageNum === 'next-ellipsis' ? <ChevronsRight className={cn(styles['jump'])} size={16} /> : null}
                </span>
              ) : (
                <PaginationLink
                  onClick={() => handlePageChange(pageNum)}
                  isActive={pageNum === page}
                  className={disabled ? 'pointer-events-none opacity-50' : hoverBgColor}
                >
                  {pageNum}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          {/* Next page */}
          <PaginationItem>
            <PaginationNext
              onClick={() => handlePageChange(page + 1)}
              className={cn(
                disabled || page >= totalPages ? 'pointer-events-none opacity-50' : hoverBgColor,
                'w-[38px]'
              )}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
export default BasePagination;
